import React, { useRef, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Icons } from "assets";
import classNames from "classnames";
import styles from "./style.module.scss";

interface StatusBarProps {
  steps: { label: string }[];
  pendingIcon: React.ReactNode;
  inProgressIcon: React.ReactNode;
  completeIcon: React.ReactNode;
  activeStep: number;
  onStepChange?: (index: number) => void;
}

const StatusBar = ({
  steps,
  pendingIcon,
  inProgressIcon,
  completeIcon,
  activeStep,
  onStepChange,
}: StatusBarProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const stepsContainerRef = useRef<HTMLDivElement>(null);
  const [greenWidth, setGreenWidth] = useState(0);
  const [showScrollButton, setShowScrollButton] = useState(true);
  const prevActiveStepRef = useRef(activeStep);
  const [justCompletedStep, setJustCompletedStep] = useState<number | null>(
    null,
  );

  useEffect(() => {
    if (activeStep > prevActiveStepRef.current) {
      setJustCompletedStep(prevActiveStepRef.current);
      const t = setTimeout(() => setJustCompletedStep(null), 600);
      prevActiveStepRef.current = activeStep;
      return () => clearTimeout(t);
    }
    prevActiveStepRef.current = activeStep;
  }, [activeStep]);

  const getIcon = (index: number) => {
    if (activeStep < 0) return pendingIcon;
    if (activeStep >= steps.length) return completeIcon;
    if (index < activeStep) return completeIcon;
    if (index === activeStep) return inProgressIcon;
    return pendingIcon;
  };

  const isStepCompleted = (index: number) => {
    if (activeStep < 0) return false;
    if (activeStep >= steps.length) return true;
    return index < activeStep;
  };

  const isStepPending = (index: number) => {
    if (activeStep < 0) return true;
    if (activeStep >= steps.length) return false;
    return index > activeStep;
  };

  const isStepInProgress = (index: number) => {
    if (activeStep < 0) return false;
    if (activeStep >= steps.length) return false;
    return index === activeStep;
  };

  const handleClick = (index: number) => {
    if (isStepCompleted(index)) return;
    if (onStepChange) onStepChange(index);
  };

  const handleScrollRight = () => {
    if (containerRef.current) {
      const scrollAmount = 200;
      containerRef.current.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const calculateProgress = () => {
      if (containerRef.current && stepsContainerRef.current) {
        const isMobile = window.innerWidth < 640;
        const containerWidth = containerRef.current.offsetWidth;

        if (isMobile) {
          const inProgressStepIndex = activeStep;

          if (activeStep <= 0) {
            setGreenWidth(0);
          } else if (activeStep >= steps.length) {
            setGreenWidth(640);
          } else if (inProgressStepIndex < steps.length) {
            const stepItems = stepsContainerRef.current.children;
            if (stepItems[inProgressStepIndex]) {
              const stepItem = stepItems[inProgressStepIndex] as HTMLElement;
              const iconContainer = stepItem.firstElementChild as HTMLElement;
              if (iconContainer) {
                const iconRect = iconContainer.getBoundingClientRect();
                const containerRect =
                  containerRef.current.getBoundingClientRect();
                const leftEdge = iconRect.left - containerRect.left;
                setGreenWidth(Math.max(0, leftEdge));
              }
            }
          }
        } else {
          const stepWidth = containerWidth / steps.length;
          const iconWidth = 22;

          if (activeStep <= 0) {
            setGreenWidth(0);
          } else if (activeStep >= steps.length) {
            setGreenWidth(containerWidth);
          } else {
            const inProgressStep = activeStep;
            const iconCenter = stepWidth * (inProgressStep + 0.5);
            const leftEdge = iconCenter - iconWidth / 2;
            setGreenWidth(Math.max(0, leftEdge));
          }
        }
      }
    };

    const timeoutId = setTimeout(() => {
      calculateProgress();
    }, 0);

    const handleResize = () => {
      setTimeout(() => {
        calculateProgress();
      }, 0);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", handleResize);
    };
  }, [activeStep, steps.length]);

  useEffect(() => {
    const checkScrollPosition = () => {
      if (containerRef.current && window.innerWidth < 640) {
        const container = containerRef.current;
        const { scrollLeft, scrollWidth, clientWidth } = container;
        const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 5;
        setShowScrollButton(!isAtEnd);
      } else {
        setShowScrollButton(false);
      }
    };

    checkScrollPosition();

    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", checkScrollPosition);
      window.addEventListener("resize", checkScrollPosition);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", checkScrollPosition);
      }
      window.removeEventListener("resize", checkScrollPosition);
    };
  }, []);

  return (
    <div ref={containerRef} className={styles.main__container}>
      <div className={styles.background_line}></div>

      <div
        className={styles.progress_line}
        style={{ width: `${greenWidth}px` }}
      ></div>

      <div ref={stepsContainerRef} className={styles.steps_container}>
        {steps.map((step, index) => {
          const completed = isStepCompleted(index);
          const inProgress = isStepInProgress(index);
          const justCompleted = justCompletedStep === index;

          return (
            <div
              key={index}
              className={classNames(styles.step_item, {
                [styles.step_item_first]: index === 0,
                [styles.step_item_last]: index === steps.length - 1,
              })}
            >
              <motion.div
                onClick={() => handleClick(index)}
                className={classNames(styles.icon_container, {
                  [styles.icon_completed]: completed,
                  [styles.icon_pending]: isStepPending(index),
                  [styles.icon_disabled]: !onStepChange,
                  [styles.icon_inProgress]: inProgress,
                  [styles.icon_justCompleted]: justCompleted,
                })}
                initial={false}
                animate={{
                  scale: justCompleted ? [1, 1.35, 1] : 1,
                  transition: justCompleted
                    ? { duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }
                    : undefined,
                }}
              >
                {inProgress && (
                  <motion.span
                    className={styles.pulse_ring}
                    animate={{
                      scale: [1, 1.8, 1],
                      opacity: [0.35, 0, 0.35],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    aria-hidden
                  />
                )}
                {inProgress ? (
                  <span className={styles.inProgressRotate}>
                    {getIcon(index)}
                  </span>
                ) : (
                  getIcon(index)
                )}
              </motion.div>
              <motion.div
                className={classNames(styles.step_label, "text-nowrap")}
                animate={
                  inProgress
                    ? {
                        opacity: [1, 0.85, 1],
                        transition: {
                          duration: 1.5,
                          repeat: Infinity,
                          ease: "easeInOut",
                        },
                      }
                    : {}
                }
              >
                {step.label}
              </motion.div>
            </div>
          );
        })}
      </div>
      <button
        onClick={handleScrollRight}
        className={classNames(styles.arrowButton, {
          [styles.arrowButtonHidden]: !showScrollButton,
        })}
      >
        <Icons.ChevRight />
      </button>
    </div>
  );
};

export default StatusBar;
